import { api } from "../api";
import L from "leaflet";
import { addBasemap } from "../utils/basemap";
import { esc } from "../utils/html";

type Practice = {
  id: number; name: string; plz: string | null; ort: string | null;
  street: string | null; houseNo: string | null;
  contactPersonStudents: string | null; phoneStudents: string | null;
  emailStudents: string | null; commentStudents: string | null;
  latitude: number | null; longitude: number | null;
  departments: { id: string; nameDe: string; color: string }[];
};

const GERMANY_CENTER: L.LatLngExpression = [51.1657, 10.4515];
const DEFAULT_ZOOM = 6;
const GREY_COLOR = "#9ca3af";

function renderPracticeCard(p: Practice): string {
  const address = [
    [p.street, p.houseNo].filter(Boolean).join(" "),
    [p.plz, p.ort].filter(Boolean).join(" "),
  ].filter(Boolean).join(", ");

  const deptBadges = p.departments.map(d =>
    `<span style="display:inline-block;padding:.15rem .5rem;border-radius:9999px;font-size:.72rem;font-weight:600;background:${esc(d.color)}22;color:${esc(d.color)};border:1px solid ${esc(d.color)}55">${esc(d.nameDe)}</span>`
  ).join(" ");

  return `<div class="card" style="padding:1.25rem">
    <div style="font-weight:700;font-size:1rem;color:#111827;margin-bottom:.4rem">${esc(p.name)}</div>
    ${p.departments.length > 0 ? `<div style="display:flex;flex-wrap:wrap;gap:.3rem;margin-bottom:.6rem">${deptBadges}</div>` : ""}
    ${address ? `<div style="font-size:.875rem;color:#6b7280;margin-bottom:.4rem">📍 ${esc(address)}</div>` : ""}
    ${p.contactPersonStudents ? `<div style="font-size:.875rem;color:#6b7280;margin-bottom:.25rem">👤 ${esc(p.contactPersonStudents)}</div>` : ""}
    ${p.phoneStudents ? `<div style="font-size:.875rem;color:#6b7280;margin-bottom:.25rem">📞 <a href="tel:${esc(p.phoneStudents)}" style="color:inherit">${esc(p.phoneStudents)}</a></div>` : ""}
    ${p.emailStudents ? `<div style="font-size:.875rem;color:#6b7280;margin-bottom:.25rem">✉️ <a href="mailto:${esc(p.emailStudents)}" style="color:inherit">${esc(p.emailStudents)}</a></div>` : ""}
    ${p.commentStudents ? `<div style="font-size:.8rem;color:#9ca3af;margin-top:.5rem;padding-top:.5rem;border-top:1px solid #f3f4f6;white-space:pre-wrap">${esc(p.commentStudents)}</div>` : ""}
  </div>`;
}

export async function render(el: HTMLElement) {
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  let map: L.Map | null = null;
  let markers: L.CircleMarker[] = [];

  try {
    const [depts, allPractices] = await Promise.all([
      api.schuelerApp.fachbereiche(),
      api.schuelerApp.praktika(),
    ]);

    let activeDept: string | null = null;
    let searchQuery = "";
    let view: "list" | "map" = "list";

    function getFiltered(): Practice[] {
      return allPractices.filter(p => {
        const matchesDept = !activeDept || p.departments.some(d => d.id === activeDept);
        const q = searchQuery.toLowerCase();
        const matchesSearch = !q
          || p.name.toLowerCase().includes(q)
          || (p.ort ?? "").toLowerCase().includes(q)
          || (p.plz ?? "").toLowerCase().includes(q);
        return matchesDept && matchesSearch;
      });
    }

    function rerenderList() {
      const listEl = el.querySelector<HTMLElement>("#practice-list")!;
      const filtered = getFiltered();
      if (filtered.length === 0) {
        listEl.innerHTML = `<div class="empty-state" style="padding:2rem 0"><p>Keine Praktikumsstellen gefunden.</p></div>`;
      } else {
        listEl.innerHTML = `<div style="display:grid;gap:1rem;grid-template-columns:repeat(auto-fill,minmax(280px,1fr))">${filtered.map(renderPracticeCard).join("")}</div>`;
      }
    }

    function markerColor(p: Practice): string {
      // Ohne aktiven Filter grau, da eine Stelle mehrere Fachbereiche haben kann.
      // Erst bei aktivem Fachbereich-Tab dessen Farbe.
      if (!activeDept) return GREY_COLOR;
      const dept = p.departments.find(d => d.id === activeDept);
      return dept?.color || GREY_COLOR;
    }

    function rerenderMap() {
      if (!map) return;
      markers.forEach(m => m.remove());
      markers = [];

      const filtered = getFiltered().filter(p => p.latitude != null && p.longitude != null);

      filtered.forEach(p => {
        const marker = L.circleMarker([p.latitude!, p.longitude!], {
          radius: 9,
          fillColor: markerColor(p),
          color: "#fff",
          weight: 2,
          fillOpacity: 0.9,
        }).addTo(map!);
        marker.bindPopup(renderPracticeCard(p), { maxWidth: 280, minWidth: 240 });
        markers.push(marker);
      });

      if (markers.length > 0) {
        const group = L.featureGroup(markers);
        map.fitBounds(group.getBounds().pad(0.15));
      } else {
        map.setView(GERMANY_CENTER, DEFAULT_ZOOM);
      }
    }

    function ensureMap() {
      if (map) return;
      const container = el.querySelector<HTMLElement>("#practice-map")!;
      map = L.map(container, { zoomControl: true }).setView(GERMANY_CENTER, DEFAULT_ZOOM);
      addBasemap(map);
    }

    function applyView() {
      const listEl = el.querySelector<HTMLElement>("#practice-list")!;
      const mapEl = el.querySelector<HTMLElement>("#practice-map")!;
      const btnList = el.querySelector<HTMLButtonElement>("#view-list")!;
      const btnMap = el.querySelector<HTMLButtonElement>("#view-map")!;
      const isMap = view === "map";
      listEl.style.display = isMap ? "none" : "";
      mapEl.style.display = isMap ? "" : "none";
      btnList.classList.toggle("active", !isMap);
      btnMap.classList.toggle("active", isMap);
      if (isMap) {
        ensureMap();
        // Leaflet braucht invalidateSize, wenn der Container vorher display:none war
        map!.invalidateSize();
        rerenderMap();
      }
    }

    function rerenderTabs() {
      const tabsEl = el.querySelector<HTMLElement>("#dept-tabs")!;
      const tabs = [{ id: null, label: `Alle (${allPractices.length})` }, ...depts.map(d => ({
        id: d.id,
        label: d.nameDe,
        color: d.color,
        count: allPractices.filter(p => p.departments.some(dep => dep.id === d.id)).length,
      }))];
      tabsEl.innerHTML = tabs.map(t => {
        const isActive = activeDept === (t.id ?? null);
        const dot = "color" in t && t.color
          ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${esc(t.color)};margin-right:.3rem"></span>`
          : "";
        const countBadge = "count" in t
          ? ` <span style="font-size:.75rem;color:#9ca3af">(${t.count})</span>`
          : "";
        return `<button data-dept="${t.id ?? ""}" class="btn ${isActive ? "btn-primary" : "btn-secondary"}" style="font-size:.8rem;padding:.3rem .75rem;white-space:nowrap">${dot}${esc(t.label)}${countBadge}</button>`;
      }).join("");

      tabsEl.querySelectorAll<HTMLButtonElement>("button[data-dept]").forEach(btn => {
        btn.addEventListener("click", () => {
          activeDept = btn.dataset.dept || null;
          rerenderTabs();
          rerenderList();
          rerenderMap();
        });
      });
    }

    el.innerHTML = `
      <div class="page-header">
        <h1>Praktikumsstellen</h1>
        <p style="color:#6b7280;margin:0">${allPractices.length} Stelle${allPractices.length !== 1 ? "n" : ""} verfügbar</p>
      </div>

      ${allPractices.length === 0 ? `<div class="empty-state"><p>Für deine Schule sind noch keine Praktikumsstellen hinterlegt.</p></div>` : `
      <div style="display:flex;flex-direction:column;gap:1rem">
        <div style="position:relative;width:100%">
          <input id="practice-search" type="search" placeholder="Suche nach Name oder Ort…"
            style="width:100%;padding:.45rem .75rem .45rem 2rem;border:1px solid #d1d5db;border-radius:.375rem;font-size:16px;background:#fff;box-sizing:border-box" />
          <span style="position:absolute;left:.6rem;top:50%;transform:translateY(-50%);color:#9ca3af;pointer-events:none">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px"><path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/></svg>
          </span>
        </div>
        <div style="display:flex">
          <div class="mode-toggle">
            <button type="button" class="mode-btn active" id="view-list">Liste</button>
            <button type="button" class="mode-btn" id="view-map">Karte</button>
          </div>
        </div>
        ${depts.length > 0 ? `<div id="dept-tabs" style="display:flex;flex-wrap:wrap;gap:.4rem"></div>` : `<div id="dept-tabs"></div>`}
        <div id="practice-list"></div>
        <div id="practice-map" style="display:none;position:relative;z-index:0;height:calc(100vh - 230px);min-height:320px;border-radius:.5rem;overflow:hidden;border:1px solid #e5e7eb"></div>
      </div>`}`;

    if (allPractices.length === 0) return;

    rerenderTabs();
    rerenderList();

    el.querySelector<HTMLInputElement>("#practice-search")?.addEventListener("input", (e) => {
      searchQuery = (e.target as HTMLInputElement).value.trim();
      rerenderList();
      rerenderMap();
    });

    el.querySelector<HTMLButtonElement>("#view-list")?.addEventListener("click", () => {
      view = "list";
      applyView();
    });
    el.querySelector<HTMLButtonElement>("#view-map")?.addEventListener("click", () => {
      view = "map";
      applyView();
    });

    // Cleanup beim Verlassen der Seite (verhindert Leaflet-Container-Leak)
    const ac = new AbortController();
    (el as any).__studentPracticesAC?.abort();
    (el as any).__studentPracticesAC = ac;
    ac.signal.addEventListener("abort", () => {
      if (map) { map.remove(); map = null; }
    });

  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
  }
}
